import type { Ref } from "react";
import { forwardRef } from "react";

import { Dropdown } from "../dropdown";
import { HintText } from "../hintText";
import { TextInput } from "../textInput";

import classes from "./styles.module.scss";
import type { IPhoneInputProps } from "./types";

import type { IOption } from "@/interfaces";
import { DEFAULT_CODE } from "@constants";
import { formatNumber, phoneUtil } from "@utils";

export const PhoneInput = forwardRef(function PhoneInput(
  props: IPhoneInputProps,
  ref: Ref<HTMLInputElement>,
) {
  const {
    hintText,
    isInvalid,
    inputSize,
    value = { code: DEFAULT_CODE, number: "" },
    onValueChange,
    readOnly,
    disabled,
    options,
    onSearchChange,
    onGetNext,
    isFetchingNextPage,
    isLoading,
    isError,
    ...rest
  } = props;

  function textInputOnChange(inputValue: string) {
    const numericValue = inputValue.replaceAll(/[^\d۰-۹]/g, "");
    const trimmedNumericValue = numericValue.slice(0, 10);

    onValueChange?.({
      ...value,
      number: formatNumber({ code: value.code, number: trimmedNumericValue }),
    });
  }

  function dropdownOnChange(dropdownValue: IOption) {
    onValueChange?.({
      number: value.number,
      code: dropdownValue.key?.toString() || DEFAULT_CODE,
    });
  }

  const countryCode = value.code
    ? phoneUtil.getRegionCodeForCountryCode(Number(value.code))
    : undefined;

  return (
    <HintText isInvalid={isInvalid} disabled={disabled} text={hintText}>
      <div className={"d-flex w-100"}>
        <TextInput
          superRootClassName={"w-100"}
          rootClassName={classes["input-container"]}
          isInvalid={isInvalid}
          readOnly={readOnly}
          disabled={disabled}
          value={formatNumber({ code: value.code, number: value.number })}
          onValueChange={textInputOnChange}
          ref={ref}
          placeholder={"9-- --- ----"}
          {...rest}
        />
        <Dropdown
          options={options}
          onSearchChange={onSearchChange}
          onGetNext={onGetNext}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          isError={isError}
          noClear
          rootClassName={classes["dropdown-container"]}
          className={classes.dropdown}
          noSearch
          isInvalid={isInvalid}
          value={{ key: value.code, value: value.code }}
          readOnly={readOnly}
          disabled={disabled}
          onValueChange={dropdownOnChange}
          flag={countryCode}
        />
      </div>
    </HintText>
  );
});
